import type { NeedCategory } from '../types/api'
import { NEED_CATEGORIES, NEED_CATEGORY_LABELS } from './taxonomy'

/**
 * The category chips on every sign-up form, and the categories they create needs or services in.
 *
 * All eleven, in the order and with the labels of the category picker on the profile screens - a
 * learner, a tutor and a parent are offered exactly what the rest of the app can match on, and
 * each of the eleven is a category the Signals-DPG network has a learner and a provider profile for.
 *
 * This used to be seven friendlier phrases (Tuition, Hobby, Competitive Exam, ...) that folded
 * Music, Sports & Fitness, AI Fluency and Languages into other headings or dropped them, so a tutor
 * who teaches music could not say so when signing up and a learner could not ask for it.
 *
 * Shared, because several places ask the same question - a learner signing up, a tutor signing up,
 * a parent signing up, and either of the first two opening their other side. One list means one
 * place to hide a category from.
 */
export const LOOKING_FOR: { label: string; category: NeedCategory }[] = NEED_CATEGORIES.map(
  (category) => ({ label: NEED_CATEGORY_LABELS[category], category }),
)

/**
 * Which of those are offered today - all eleven.
 *
 * Kept as a filter rather than deleted rows, so hiding one again is a single entry here. This is
 * only ever a display rule: `POST /api/needs` and `POST /api/services` accept every category.
 */
const HIDDEN_LOOKING_FOR: NeedCategory[] = []

export const VISIBLE_LOOKING_FOR = LOOKING_FOR.filter(
  (o) => !HIDDEN_LOOKING_FOR.includes(o.category),
)

/**
 * What a provider on the map is, in a word or two - the line under their name.
 *
 * <p>The map now carries schools, colleges and companies beside tutors, and "Tutor / Coach" under
 * a school's name would be wrong on the one line that says what it is.
 */
export function providerKindLabel(entityKind: string): string {
  switch (entityKind) {
    case 'CENTRE':
      return 'Centre'
    case 'COUNSELLOR':
      return 'Counsellor'
    case 'SCHOOL':
      return 'School'
    case 'COLLEGE':
      return 'College'
    case 'COMPANY':
      return 'Corporate'
    default:
      return 'Tutor / Coach'
  }
}
